import type { GroupedSelectPopoverProps } from '../components';

import { useDependentDisableOption } from './useDependentDisableOption';
import { useControlDataChange } from '~/presentation/reports/common';
import { usePopoverResizeObserver } from '~/hooks';
import { useSearchItems } from './useSearchItems';
import { useGroupedSelect } from '../stores';
import { buildControlUI } from '../utils';
import { useState, useMemo } from 'react';

interface IUseItemSelectorProps {
  control: GroupedSelectPopoverProps;
  togglePopoverActive: (active: boolean) => void;
  active: boolean;
}

const HEIGHT_ITEM = 32;
const PADDING = 24;
const HEIGHT_GROUP_NAME = 20;
export const useItemSelector = ({
  control,
  togglePopoverActive,
  active,
}: IUseItemSelectorProps) => {
  const { defaultValue, config, controls, connector, dependentControls } = control;
  const { controlSettingUI, defaultControls } = config || {};

  const { emitControlChange, emitControlUIChange, emitResetControlData } = useControlDataChange();
  const { optionsGroup, dependentDisabledValues } = useDependentDisableOption({
    dependentControls,
    optionsGroup: config?.optionsGroup,
  });

  const { itemHovered, setItemHovered } = useGroupedSelect();

  const [queryValue, setQueryValue] = useState('');
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>(
    typeof defaultValue === 'string' ? [defaultValue] : defaultValue || [],
  );

  const { listItems, hasItems } = useSearchItems({
    queryValue,
    optionsGroup: optionsGroup || [],
  });

  const totalOptions = useMemo(() => {
    return optionsGroup?.flatMap((group) => group.options).length || 0;
  }, [optionsGroup]);

  const disableApplyButton = useMemo(() => {
    if (typeof defaultValue === 'string') return true;

    const isFullySelected =
      defaultValue?.length === totalOptions && tempSelectedItems.length === totalOptions;
    const hasNoTempSelection = !tempSelectedItems.length;

    return isFullySelected || hasNoTempSelection;
  }, [tempSelectedItems, totalOptions, defaultValue]);

  const handleHoverMetric = (value?: string) => {
    if (!value) return;

    setItemHovered(value);
  };

  const handleApplySelection = () => {
    const { newControls, removedIds } = buildControlUI({
      controls,
      tempSelectedItems,
      defaultControls,
      controlSettingUI,
    });

    if (removedIds.length) {
      emitResetControlData(removedIds);
    }
    emitControlChange({ [control.id]: tempSelectedItems });
    emitControlUIChange({ [control.id]: { controls: newControls } });

    if (connector && !tempSelectedItems.includes(`${connector.value}`)) {
      emitControlChange({ [connector.key]: tempSelectedItems[0] });
    }
    togglePopoverActive(false);
  };

  const disabledItems = useMemo(() => {
    return (
      optionsGroup
        ?.flatMap((group) => group.options)
        .filter((option) => option.disabled)
        .map((option) => option.value) ?? []
    );
  }, [optionsGroup]);

  const disableClearButton = useMemo(() => {
    return tempSelectedItems.length <= disabledItems.length;
  }, [disabledItems, tempSelectedItems]);

  const handleClearSelection = () => {
    setTempSelectedItems(disabledItems);
  };

  const height = useMemo(() => {
    const defaultValue = {
      group: 0,
      item: 0,
    };
    const { group, item } =
      optionsGroup?.reduce((acc, { options, title }) => {
        return {
          group: title ? acc.group + 1 : acc.group,
          item: acc.item + options.length,
        };
      }, defaultValue) || defaultValue;

    const itemHeight = item * HEIGHT_ITEM;
    const groupHeight = group * HEIGHT_GROUP_NAME;
    const groupPadding = group * PADDING;
    const outerPadding = PADDING * 2;

    return itemHeight + groupHeight + groupPadding + outerPadding;
  }, [optionsGroup]);

  const [maxHeight, setMaxHeight] = useState(628);
  usePopoverResizeObserver({
    active,
    onHeightChange: (popoverHeight) => {
      if (!popoverHeight) return;
      const headerAndFooterHeight = 93;
      const contentHeight = popoverHeight - headerAndFooterHeight;
      if (contentHeight < 628) {
        setMaxHeight(contentHeight);
      }
    },
  });

  const handleChangeSelection = (value: string) => {
    setTempSelectedItems([value]);
    emitControlChange({ [control.id]: value });
    togglePopoverActive(false);
  };

  return {
    listItems,
    hasItems,
    tempSelectedItems,
    handleHoverMetric,
    itemHovered,
    setTempSelectedItems,
    queryValue,
    handleChangeSelection,
    setItemHovered,
    setQueryValue,
    height,
    maxHeight,
    disableClearButton,
    handleClearSelection,
    handleApplySelection,
    disableApplyButton,
    dependentDisabledValues,
  };
};
